import { useState } from 'react';
import type { ConfigScope, EnvironmentConfigDTO, ImportDotenvRequest, ImportPreviewDTO, TargetDTO, VpsDTO } from '@devrobot/shared';
import { Lock } from 'lucide-react';
import { api, seg } from '../../../api/client';
import { qk, useApiMutation } from '../../../api/queries';
import { ErrorAlert } from '../../../components/ErrorAlert';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toaster';
import { Haptic } from '../../../kit/haptics';
import { haptic } from '../../../kit/native';
import { ScopePicker, scopeValid } from './ScopePicker';

const ACTION_BADGE = { create: 'badge badge-success badge-soft badge-sm', replace: 'badge badge-warning badge-soft badge-sm', unchanged: 'badge badge-ghost badge-sm' } as const;
const onMutationError = () => haptic('error');

/** Import a .env file into an explicitly selected scope, with a review step before storing. */
export function ImportModal({ projectKey, cfg, vpsList, targets, onClose }: { projectKey: string; cfg: EnvironmentConfigDTO; vpsList: VpsDTO[]; targets: TargetDTO[]; onClose: () => void }) {
  const toast = useToast();
  const env = cfg.environment;
  const [content, setContent] = useState('');
  const [scope, setScope] = useState<ConfigScope>({ kind: 'environment' });
  const [preview, setPreview] = useState<ImportPreviewDTO | null>(null);
  const [include, setInclude] = useState<Set<string>>(new Set());
  const url = `/api/v1/projects/${seg(projectKey)}/environments/${seg(env)}/config-import`;
  const run = useApiMutation((body: ImportDotenvRequest) => api.post<ImportPreviewDTO>(url, body), {
    invalidate: (body) => (body.dryRun ? [] : [qk.config(projectKey, env), qk.environments(projectKey), qk.targets(projectKey), ['target']]),
  });

  const doPreview = () =>
    run.mutate(
      { content, scope, dryRun: true },
      {
        onSuccess: (r) => {
          setPreview(r);
          setInclude(new Set(r.entries.filter((e) => e.action !== 'unchanged').map((e) => e.name)));
        },
        onError: onMutationError,
      },
    );

  const apply = () =>
    run.mutate(
      { content, scope, dryRun: false, include: [...include] },
      {
        onSuccess: () => {
          toast.success(`${include.size} value${include.size === 1 ? '' : 's'} imported into ${env}`, { description: 'Running targets keep their applied configuration until you apply the change.' });
          onClose();
        },
        onError: onMutationError,
      },
    );

  const previewDisabled = !content.trim() || !scopeValid(scope) || run.isPending;
  const applyDisabled = include.size === 0 || run.isPending;

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    setContent(await file.text());
    setPreview(null);
  };

  return (
    <Modal title={`Import a .env file into ${env}`} onClose={onClose} size="xl">
      <div className="space-y-3">
        <p className="text-sm text-base-content/65">Values are stored in DevRobot (secrets encrypted). Your repository is not modified. Review the result before anything is saved.</p>
        {!preview ? (
          <div key="edit" className="animate-rise space-y-3">
            <ScopePicker env={env} value={scope} onChange={setScope} vpsList={vpsList} targets={targets} />
            <fieldset className="fieldset">
              <legend className="fieldset-legend">.env content</legend>
              <input type="file" accept=".env,text/plain" className="file-input file-input-sm w-full" onChange={(e) => void readFile(e.target.files?.[0])} aria-label="Choose a .env file" />
              <textarea
                className="textarea mt-2 w-full font-mono text-xs"
                rows={10}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={'DATABASE_URL=postgres://…\nSMTP_PASSWORD="…"'}
                spellCheck={false}
                autoComplete="off"
                aria-label=".env content"
                style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
              />
              <p className="label whitespace-normal">Content is masked on screen. KEY=value, export KEY=value, quotes and # comments are supported.</p>
            </fieldset>
          </div>
        ) : (
          <div key="review" className="animate-rise space-y-3">
            {preview.errors.length > 0 ? (
              <div role="alert" className="alert alert-warning alert-soft text-sm">
                <div>
                  <p className="font-medium">Lines that could not be read (skipped)</p>
                  <ul className="list-disc pl-5">
                    {preview.errors.map((e, i) => (
                      <li key={i}>
                        line {e.line}: {e.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
            <div className="overflow-x-auto rounded-field border border-hairline">
              <table className="table table-sm">
                <thead>
                  <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                    <th>
                      <span className="sr-only">Include</span>
                    </th>
                    <th>Variable</th>
                    <th>Result</th>
                    <th>Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.entries.map((e) => (
                    <tr key={e.name} className="hover:bg-base-200/50">
                      <td>
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm"
                          checked={include.has(e.name)}
                          disabled={e.action === 'unchanged'}
                          aria-label={`Import ${e.name}`}
                          onChange={(ev) => {
                            const next = new Set(include);
                            if (ev.target.checked) next.add(e.name);
                            else next.delete(e.name);
                            setInclude(next);
                          }}
                        />
                      </td>
                      <td className="font-mono text-sm">{e.name}</td>
                      <td>
                        <span className={ACTION_BADGE[e.action]}>{e.action === 'replace' ? 'replaces current value' : e.action === 'create' ? 'new' : 'unchanged'}</span>
                      </td>
                      <td className="text-sm">
                        {e.secret ? (
                          <span className="inline-flex items-center gap-1">
                            <Lock className="size-3" aria-hidden="true" /> secret
                          </span>
                        ) : (
                          'plain'
                        )}
                        {!e.declared ? <span className="badge badge-ghost badge-xs ml-1">not in manifest</span> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-base-content/65">
              <span className="tabular-nums">{include.size}</span> of {preview.entries.length} selected. Variables not declared in the manifest are delivered to the application service only.
            </p>
          </div>
        )}
        <ErrorAlert error={run.error} title={preview ? 'The import was not applied' : 'The file could not be previewed'} />
        <div className="modal-action">
          {preview ? (
            <button type="button" className="btn btn-ghost" onClick={() => setPreview(null)} disabled={run.isPending}>
              Back
            </button>
          ) : (
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
          )}
          {preview ? (
            <Haptic disabled={applyDisabled}>
              <button type="button" className="btn btn-primary" disabled={applyDisabled} onClick={apply}>
                {run.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : null}
                Import {include.size} value{include.size === 1 ? '' : 's'}
              </button>
            </Haptic>
          ) : (
            <Haptic disabled={previewDisabled}>
              <button type="button" className="btn btn-primary" disabled={previewDisabled} onClick={doPreview}>
                {run.isPending ? <span className="loading loading-spinner loading-sm" aria-hidden="true" /> : null}
                Review import
              </button>
            </Haptic>
          )}
        </div>
      </div>
    </Modal>
  );
}
